/**
 * La importación en curso y su resultado (SPEC-BD §6.2, paso «resultado», y el detalle de una carga).
 *
 * Es sólo la vista: el polling de la carga lo hace la página (la dueña del estado). Una `fallida` sólo
 * se reintenta tal cual (la misma huella): ya importó una parte, y cambiarle algo dejaría contactos con
 * una declaración o unas etiquetas que la carga ya no tiene. Si ya escribió contactos tampoco se
 * descarta (el backend lo rechaza): lo que ya importó se saca con «Borrar los datos de esta carga», el
 * botón de la pantalla de la carga. El texto sigue a `accionPeligroCarga`, que decide ese botón.
 *
 * Si la carga se subió desde el asistente de una campaña («Subir un Excel»), al terminar la acción principal es
 * «Usar en la campaña» (`usarEnCampana`); lo demás queda como secundario.
 */

import { Link } from "react-router-dom";
import { CheckCircle2, Download, Loader2, RefreshCw, Send, Upload, Users, UsersRound, XCircle } from "lucide-react";
import type { CargaPublica } from "../../types.base";
import { num, plural } from "../../../campana-nueva/utils/format";
import { accionPeligroCarga } from "../../logic/base";
import { R_BASE } from "./comun";
import { R_PUBLICO } from "../../../publico/logic/publico";

interface Props {
  carga: CargaPublica;
  onReintentar: () => void;
  reintentando: boolean;
  errorReintento: string;
  /** El reintento dio ARCHIVO_VENCIDO: ya no hay archivo, sólo queda subirlo de nuevo. */
  archivoVencido: boolean;
  onDescargarErrores: () => void;
  descargando: boolean;
  errorDescarga: string;
  onSubirOtro: () => void;
  /** El polling se detuvo tras 3 errores seguidos. */
  pollDetenido?: boolean;
  onReintentarPoll?: () => void;
  /** Una persona de un cliente: ve qué pasó, sin reintentar ni subir otro (la Base la carga el equipo). */
  soloLectura?: boolean;
  /** La carga vino del asistente de una campaña: usarla ahí (armar su público y volver). */
  usarEnCampana?: { onUsar: () => void; usando: boolean; error: string };
}

const BOTON_PRINCIPAL = "h-10 px-4 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-2";
const BOTON_SECUNDARIO = "h-10 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2";

export function ProgresoCarga(p: Props) {
  const c = p.carga;
  const id = encodeURIComponent(c.id);

  if (c.estado === "importando") {
    const total = c.progreso?.total ?? 0;
    const procesadas = Math.min(c.progreso?.procesadas ?? 0, total || Infinity);
    const pct = total > 0 ? Math.min(100, Math.floor((procesadas / total) * 100)) : 0;
    return (
      <div className="space-y-3" aria-live="polite">
        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Loader2 size={15} className="animate-spin text-sky-600" /> Importando: {num(procesadas)} de {num(total)} {plural(total, "contacto", "contactos")}
        </p>
        <div className="h-2.5 rounded-full bg-sky-100 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <div className="h-full rounded-full bg-sky-600 transition-all duration-700" style={{ width: `${Math.max(2, pct)}%` }} />
        </div>
        <p className="text-xs text-slate-500">Podés cerrar esta pantalla: la importación sigue sola.</p>
        {p.pollDetenido && (
          <p className="text-xs text-amber-700">
            Dejamos de actualizar porque el servidor no responde.{" "}
            {p.onReintentarPoll && <button type="button" onClick={p.onReintentarPoll} className="font-semibold underline">Reintentar</button>}
          </p>
        )}
      </div>
    );
  }

  if (c.estado === "fallida") {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700 space-y-3" role="alert">
        <p className="font-semibold flex items-center gap-2"><XCircle size={16} /> La importación no terminó: {c.lastError || "no sabemos por qué."}</p>
        {p.soloLectura ? (
          <p>La carga la maneja el equipo de Deenex.</p>
        ) : p.archivoVencido ? (
          <>
            <p>El archivo de esta carga venció (se guarda 24 h). Subilo de nuevo.</p>
            <button type="button" onClick={p.onSubirOtro} className="h-9 px-3 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 inline-flex items-center gap-1.5">
              <Upload size={14} /> Subir otro archivo
            </button>
          </>
        ) : (
          <>
            {p.errorReintento && <p>{p.errorReintento}</p>}
            <button type="button" onClick={p.onReintentar} disabled={p.reintentando}
              className="h-9 px-3 rounded-xl bg-red-600 text-white font-semibold hover:bg-red-700 inline-flex items-center gap-1.5 disabled:opacity-50">
              {p.reintentando ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Reintentar
            </button>
            {accionPeligroCarga(c) === "borrar_datos" ? (
              <p className="text-xs text-red-600">
                Se reintenta tal cual y lo que ya se importó no se duplica. Para sacar lo que ya importó, usá «Borrar los datos de esta carga», en la pantalla de la carga.
              </p>
            ) : (
              <p className="text-xs text-red-600">Todavía no importó ningún contacto. Para cambiar algo, descartá esta carga y subí el archivo de nuevo.</p>
            )}
          </>
        )}
      </div>
    );
  }

  if (c.estado === "importada") {
    const r = c.resultado;
    return (
      <div className="space-y-4">
        <p className="text-sm font-semibold text-emerald-700 flex items-center gap-2"><CheckCircle2 size={16} /> Importación terminada</p>
        {r && (
          <dl className="grid gap-3 grid-cols-2 sm:grid-cols-4 text-sm">
            <Dato label="Contactos nuevos" valor={r.nuevos} />
            <Dato label="Actualizados" valor={r.actualizados} />
            <Dato label="Sin cambios" valor={r.sinCambios} />
            <Dato label="Teléfonos inválidos" valor={r.telefonosInvalidos} />
            <Dato label="Con consentimiento" valor={r.conConsentimiento} />
            <Dato label="Sin consentimiento" valor={r.sinConsentimiento} />
            <Dato label="Dijeron que no" valor={r.dijeronQueNo} />
            <Dato label="En no contactar" valor={r.enNoContactar} />
          </dl>
        )}
        {c.erroresTotal > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={p.onDescargarErrores} disabled={p.descargando}
              className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
              {p.descargando ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Descargar el detalle de errores
            </button>
            <span className="text-xs text-slate-500">{num(c.erroresTotal)} {c.erroresTotal === 1 ? "fila con problemas" : "filas con problemas"}</span>
          </div>
        )}
        {p.errorDescarga && <p className="text-xs text-red-600" role="alert">{p.errorDescarga}</p>}
        {/* la ficha de cada persona es la de Público (la Base salió del menú el 17/10/2026) */}
        <p className="text-xs text-slate-500">Aparecen en Público cuando se vuelve a armar: solo, cada 30 minutos, o con «Actualizar».</p>
        {p.usarEnCampana && (
          <div className="rounded-xl bg-violet-50 px-4 py-3 space-y-2">
            <p className="text-sm text-violet-900">
              La campaña sale a los contactos de este archivo que pueden recibir: el número final lo da el chequeo de la campaña.
            </p>
            <button type="button" onClick={p.usarEnCampana.onUsar} disabled={p.usarEnCampana.usando} className={`${BOTON_PRINCIPAL} disabled:opacity-50`}>
              {p.usarEnCampana.usando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Usar en la campaña
            </button>
            {p.usarEnCampana.error && <p className="text-xs text-red-600" role="alert">{p.usarEnCampana.error}</p>}
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <Link to={R_PUBLICO} className={p.usarEnCampana ? BOTON_SECUNDARIO : BOTON_PRINCIPAL}>
            <Users size={15} /> Ver en Público
          </Link>
          {/* desde una campaña, el público de la carga lo arma «Usar en la campaña» */}
          {!p.usarEnCampana && (
            <Link to={`${R_BASE}/publicos/nuevo?carga=${id}`} className={BOTON_SECUNDARIO}>
              <UsersRound size={15} /> Crear un público con esta carga
            </Link>
          )}
          <button type="button" onClick={p.onSubirOtro} className={BOTON_SECUNDARIO}>
            <Upload size={15} /> Subir otro archivo
          </button>
        </div>
      </div>
    );
  }

  return null;
}

function Dato({ label, valor }: { label: string; valor: number }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2">
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className="text-lg font-semibold text-slate-900">{num(valor)}</dd>
    </div>
  );
}

export default ProgresoCarga;
